<template>
  <div class="my-activity" redesign>
    <!-- Header -->
    <div class="local-hero">
      <h1 class="local-hero-header" v-translate>My Activity</h1>
    </div>

    <!-- Loading spinner -->
    <div v-if="loading" class="form-container">
      <LoadSpinner :color="`#${organizationDetails.darkBrandColor}`" />
    </div>

    <!-- Activity tabs -->
    <div v-else class="form-container card-shape my-activity-card">
      <div class="my-activity-card-header">
        <div
          v-if="organizationDetails.isDefaultRequestOrganization"
          class="my-activity-card-toggle"
          :class="{ active: viewRequests === 'requests' }"
          @click="toggleViewRequests('requests')"
          v-translate
        >
          Hardship Requests
        </div>
        <div
          v-if="organizationDetails.isReproductiveRightsOrganization"
          class="my-activity-card-toggle"
          :class="{ active: viewRequests === 'reproductive-rights' }"
          @click="toggleViewRequests('reproductive-rights')"
          v-translate
        >
          Inclusive Care Requests
        </div>
        <div
          v-if="organizationDetails.donationsEnabled"
          class="my-activity-card-toggle"
          :class="{ active: viewRequests === 'gifts' }"
          @click="toggleViewRequests('gifts')"
          v-translate
        >
          Gifts
        </div>
        <div
          v-if="organizationDetails.isTcrOrganization || organizationDetails.isNorthwestern"
          class="my-activity-card-toggle"
          :class="{ active: viewRequests === 'stipend-requests' }"
          @click="toggleViewRequests('stipend-requests')"
          v-translate
        >
          Stipend Requests
        </div>
      </div>

      <!-- Request view -->
      <div v-if="viewRequests === 'requests'" class="my-activity-list">
        <div v-if="requestList.list.length > 0" class="my-activity-list-header hidden-mobile">
          <div>Amount</div>
          <div>Status</div>
          <div>Date</div>
        </div>
        <div class="request-container" v-for="(activity, i) in requestList.list" :key="i">
          <ActivityCard :activity="activity" @show-grant-limit-modal="showGrantLimitModal" />
        </div>

        <span v-if="requestList.list.length == 0" class="no-activity" v-translate
          >No Activity History Available</span
        >

        <!-- Pagination -->
        <div class="pagination-container">
          <button
            v-if="requestList.pagination.hasPrevPage"

            class="form-button pagination-button"
            @click="() => requestList.prevPage()"
            :disabled="!requestList.pagination.hasPrevPage"
          >
            <font-awesome-icon
              class="pagination-icons"
              :icon="['fa', 'chevron-left']"
              :style="{ fontSize: '24px' }"
            ></font-awesome-icon>
          </button>

          <button
            v-if="requestList.pagination.hasNextPage"
            class="form-button pagination-button"
            @click="() => requestList.nextPage()"
            :disabled="!requestList.pagination.hasNextPage"
          >
            <font-awesome-icon
              class="pagination-icons"
              :icon="['fa', 'chevron-right']"
              :style="{ fontSize: '25px' }"
            ></font-awesome-icon>
          </button>
        </div>
      </div>

      <!-- Repro rights -->
      <div v-if="viewRequests === 'reproductive-rights' && organizationDetails.isReproductiveRightsOrganization" class="my-activity-list">
        <div v-if="reproRightsList.list.length > 0" class="my-activity-list-header hidden-mobile">
          <div>Amount</div>
          <div>Status</div>
          <div>Date</div>
        </div>
        <div class="request-container" v-for="(activity, i) in reproRightsList.list" :key="i">
          <ActivityCard :activity="activity" @show-grant-limit-modal="grantLimitModal" />
        </div>

        <span v-if="reproRightsList.list.length == 0" class="no-activity" v-translate
          >No Activity History Available</span
        >

        <!-- Pagination -->
        <div class="pagination-container">
          <button
            v-if="reproRightsList.pagination.hasPrevPage"
            class="form-button pagination-button"
            @click="() => reproRightsList.prevPage()"
            :disabled="!reproRightsList.pagination.hasPrevPage"
          >
            <font-awesome-icon
              class="pagination-icons"
              :icon="['fa', 'chevron-left']"
              :style="{ fontSize: '24px' }"
            ></font-awesome-icon>
          </button>

          <button
            v-if="reproRightsList.pagination.hasNextPage"
            class="form-button pagination-button"
            @click="() => reproRightsList.nextPage()"
            :disabled="!reproRightsList.pagination.hasNextPage"
          >
            <font-awesome-icon
              class="pagination-icons"
              :icon="['fa', 'chevron-right']"
              :style="{ fontSize: '25px' }"
            ></font-awesome-icon>
          </button>
        </div>
      </div>

      <!-- Donations view -->
      <div v-if="viewRequests === 'gifts'" class="my-activity-list">
        <div
          class="donation-container"
          v-for="(donation, i) in recurringDonationList.list"
          :key="i"
        >
          <DonationCard :donation="donation" />
        </div>

        <span v-if="recurringDonationList.list.length == 0" class="no-activity" v-translate
          >No Activity History Available</span
        >

        <!-- Pagination -->
        <div class="pagination-container">
          <button
            v-if="recurringDonationList.pagination.hasPrevPage"
            class="form-button pagination-button"
            @click="() => recurringDonationList.prevPage()"
            :disabled="!recurringDonationList.pagination.hasPrevPage"
          >
            <font-awesome-icon
              class="pagination-icons"
              :icon="['fa', 'chevron-left']"
              :style="{ fontSize: '24px' }"
            ></font-awesome-icon>
          </button>

          <button
            v-if="recurringDonationList.pagination.hasNextPage"
            class="form-button pagination-button"
            @click="() => recurringDonationList.nextPage()"
            :disabled="!recurringDonationList.pagination.hasNextPage"
          >
            <font-awesome-icon
              class="pagination-icons"
              :icon="['fa', 'chevron-right']"
              :style="{ fontSize: '25px' }"
            ></font-awesome-icon>
          </button>
        </div>
      </div>

      <!-- Stipend Request view -->
      <div v-if="viewRequests === 'stipend-requests'" class="my-activity-list">
        <div v-if="stipendRequestList.list.length > 0" class="my-activity-list-header hidden-mobile">
          <div>Program Month</div>
          <div>Amount</div>
          <div>Status</div>
          <div>Date</div>
        </div>
        <div class="request-container" v-for="(stipendRequest, i) in stipendRequestList.list" :key="i">
          <StipendRequestCard :stipendRequest="stipendRequest" />
        </div>

        <span v-if="stipendRequestList.list.length == 0" class="no-activity" v-translate
          >No Activity History Available</span
        >

        <!-- Pagination -->
        <div class="pagination-container">
          <button
            v-if="stipendRequestList.pagination.hasPrevPage"

            class="form-button pagination-button"
            @click="() => stipendRequestList.prevPage()"
            :disabled="!stipendRequestList.pagination.hasPrevPage"
          >
            <font-awesome-icon
              class="pagination-icons"
              :icon="['fa', 'chevron-left']"
              :style="{ fontSize: '24px' }"
            ></font-awesome-icon>
          </button>

          <button
            v-if="stipendRequestList.pagination.hasNextPage"
            class="form-button pagination-button"
            @click="() => stipendRequestList.nextPage()"
            :disabled="!stipendRequestList.pagination.hasNextPage"
          >
            <font-awesome-icon
              class="pagination-icons"
              :icon="['fa', 'chevron-right']"
              :style="{ fontSize: '25px' }"
            ></font-awesome-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Grant limit modal -->
    <GrantLimitModal :show="grantLimitModal" />

    <!-- Required post-registration onboarding surveys -->
    <RequiredOnboardingSurveysModal :show="requiredOnboardingSurveysModal && !grantLimitModal" />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import Action from '../services/actions'
import Donation from '@/services/donations'
import CollectionManager from '../services/collectionManager'
import StipendRequest from '../services/stipendRequests'
import DonationCard from '@/components/myActivity/DonationCard'
import ActivityCard from '@/components/myActivity/ActivityCard'
import StipendRequestCard from '@/components/myActivity/StipendRequestCard'
import LoadSpinner from '@/components/LoadSpinner'
import Modal from '@/components/Modal'
import GrantLimitModal from '@/views/common/GrantLimitModal'
import RequiredOnboardingSurveysModal from '@/views/common/RequiredOnboardingSurveysModal'

export default {
  name: 'MyActivity',
  components: {
    ActivityCard,
    LoadSpinner,
    DonationCard,
    StipendRequestCard,
    Modal,
    GrantLimitModal,
    RequiredOnboardingSurveysModal,
  },
  data() {
    return {
      today: new Date(),
      // The MyActivityList should return a list of started requests
      requestList: CollectionManager.create({
        ModelClass: Action,
        filters: { ordering: '-datetime_created', actionType: 'REQUEST_STARTED', excludeCategoryName: 'Reproductive Rights' },
      }),
      reproRightsList: CollectionManager.create({
        ModelClass: Action,
        filters: {
          ordering: '-datetime_created',
          actionType: 'REQUEST_STARTED',
          categoryName: 'Reproductive Rights',
        },
      }),
      recurringDonationList: CollectionManager.create({
        ModelClass: Donation,
        filters: { frequency: 'RECURRING' },
      }),
      stipendRequestList: CollectionManager.create({
        ModelClass: StipendRequest,
        filters: {
          ordering: 'datetime_created',
        },
      }),
      loading: true,
      viewRequests: 'requests',
      grantLimitModal: false,
    }
  },
  async created() {
    await this.refresh()
    
    this.loading = false

    if (!this.organizationDetails.isDefaultRequestOrganization && this.organizationDetails.isReproductiveRightsOrganization) {
      this.viewRequests = 'reproductive-rights'
    }

    if (!this.organizationDetails.isDefaultRequestOrganization && !this.organizationDetails.isReproductiveRightsOrganization) {
      this.viewRequests = 'gifts'
    }

    if (this.organizationDetails.isTcrOrganization || this.organizationDetails.isNorthwestern) {
      this.viewRequests = 'stipend-requests'
    }
  },
  methods: {
    async refresh() {
      await this.requestList.refresh()
      await this.reproRightsList.refresh()
      await this.recurringDonationList.refresh()
      await this.stipendRequestList.refresh()
    },
    toggleViewRequests(val) {
      this.viewRequests = val
    },
    getScopedOrgProperty(propName) {
      // Empty strings and zero are considered null/undefined/not-set
      const orgValue = this.organizationDetails && this.organizationDetails[propName]
      const subOrgValue = this.user.subOrganizationRef && this.user.subOrganizationRef[propName]

      return subOrgValue || orgValue
    },
    showGrantLimitModal() {
      this.grantLimitModal = true
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    maxedGrantLimit() {
      const amountLimit = this.getScopedOrgProperty('grantLimit')
      const countLimit = this.getScopedOrgProperty('grantCountLimit')

      const hasLimits = amountLimit || countLimit

      if (!hasLimits) { return false }

      // Lifetime grant limit
      if (this.maxedLifetimeGrantLimit) {
        return true 
      }

      const currentAmount = this.user.totalPayoutsSinceLastGrantLimitReset
      const currentCount = this.user.totalNumberPayoutsSinceLastGrantLimitReset

      const overAmount = amountLimit ? currentAmount >= amountLimit : false
      const overCount = countLimit ? currentCount >= countLimit : false

      return overAmount || overCount
    },
    maxedLifetimeGrantLimit() {
      const amountLimitLifetime = this.getScopedOrgProperty('grantLimitLifetime')
      const currentAmountLifetime = this.user.totalPayouts
      if(amountLimitLifetime) {
        return currentAmountLifetime >= amountLimitLifetime
      }

      return false
    },
    requiredOnboardingSurveysModal() {
      if(!this.user)
        return false

      if(this.user.isStaff || this.user.isAdmin)
        return false

      return !this.user.hasCompletedRequiredOnboardingSurveys
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/flex-box';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
@import '@/styles/mixins/responsive';
@import '@/styles/grant-circles';

.my-activity {
  display: flex;
  flex-direction: column;

  .my-activity-card {
    @extend .constrain-tablet-and-up;

    padding: 0;

    .my-activity-card-header {
      border-bottom: 1px solid #dfdfe0;
      padding: 0 1.5rem;
    }

    .my-activity-card-toggle {
      color: #212b36;
      cursor: pointer;
      display: inline-block;
      padding: 1rem 2rem 1rem 2rem;
      font-size: 0.75rem;
      font-weight: bold;
      text-transform: uppercase;
      min-width: 33%;
      text-align: center;

      &.active {
        border-bottom: 2px solid black;
      }
    }

    .my-activity-list {
      padding: 1.5rem;
    }

    .my-activity-list-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: 1rem 0.5rem;
      border-bottom: 2px solid #dfdfe0;

      div {
        font-weight: bold;
      }

      div:nth-child(1) {
        width: 15%;
      }

      div:nth-child(2) {
        width: 35%;
      }

      div:nth-child(3) {
        width: 20%;
      }
    }

    .request-container,
    .donation-container {
      border-bottom: 1px solid #dfdfe0;
      margin-bottom: 1rem;

      &:last-of-type {
        margin-bottom: 4.5rem;
      }

      @include tablet-and-up-screen {
        margin-bottom: 0;
      }

      .no-activity {
        padding: 1rem;
      }
    }

    .pagination-container {
      display: flex;
      justify-content: space-between;
    }

    .pagination-button {
      background: #000;
    }
  }
}

.maxedModalSection {
  margin: 1rem 0 0 0;
  justify-content: start;
  align-items: center;
}
</style>
